import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { IonButton } from '@ionic/angular/standalone';
import { PlatformService } from '../../../core/services/platform.service';
import { ScanCloseReason, ScanResult, ScanService } from '../../../core/services/scan.service';

export type { ScanCloseReason };

/**
 * Full-screen barcode scanner overlay.
 *
 * @remarks
 * Mount this component inside an `@if` block; it starts scanning in
 * `ngAfterViewInit` and stops in `ngOnDestroy`, so the camera lifecycle
 * is fully tied to the element's DOM lifetime.
 *
 * **Platform-aware start-up:**
 * - Native (iOS/Android): starts scanning immediately — the native scanner
 *   manages its own camera UI on top of this overlay. The awaited
 *   `ScanService.scan()` call is bounded by `ScanService`'s own
 *   `NATIVE_SCAN_TIMEOUT_MS` (MOB-293), so this overlay's hint/Cancel chrome
 *   can never be stranded indefinitely even if the native scanner's promise
 *   fails to settle after a dismiss.
 * - Web/PWA: defers scan by one tick so Angular finishes rendering the
 *   `<video>` element before ZXing tries to attach to it.
 *
 * @example
 * ```html
 * @if (scanOpen()) {
 *   <app-scan-overlay
 *     hint="Point camera at a work-order barcode"
 *     (scanned)="onScanResult($event)"
 *     (closed)="onScanClosed($event)" />
 * }
 * ```
 */
@Component({
  selector: 'app-scan-overlay',
  standalone: true,
  imports: [IonButton],
  templateUrl: './scan-overlay.component.html',
  styleUrl: './scan-overlay.component.scss',
})
export class ScanOverlayComponent implements AfterViewInit, OnDestroy {
  /** `<video>` element used by ZXing for live camera preview (web/PWA path). */
  @ViewChild('scanVideo') private videoRef?: ElementRef<HTMLVideoElement>;

  /** Hint text shown below the scan guide frame. */
  @Input() hint = 'Point camera at a barcode';

  /**
   * Emitted when a barcode is successfully decoded.
   * The parent should close the overlay (set the `@if` condition to false)
   * and act on the result.
   */
  @Output() scanned = new EventEmitter<ScanResult>();

  /**
   * Emitted when the overlay closes without a successful result.
   * `'cancelled'` — user pressed Cancel.
   * `'error'`     — scan decode/permission failure.
   * The parent should close the overlay and show an appropriate toast if needed.
   */
  @Output() closed = new EventEmitter<ScanCloseReason>();

  private readonly scanSvc  = inject(ScanService);
  private readonly platform = inject(PlatformService);

  /** `true` on iOS/Android — drives the `scan-overlay--native` CSS modifier. */
  readonly isNative = this.platform.isNative;

  /** @inheritdoc */
  ngAfterViewInit(): void {
    if (this.platform.isNative) {
      void this.doScan();
    } else {
      // Defer one tick so Angular finishes rendering <video> before ZXing attaches
      setTimeout(() => void this.doScan(), 0);
    }
  }

  /** @inheritdoc */
  ngOnDestroy(): void {
    this.scanSvc.stopScan();
  }

  /**
   * Handles the Cancel button — stops the active scan and emits `closed('cancelled')`.
   * Safe to call even when no scan is in progress.
   */
  onCancel(): void {
    this.scanSvc.stopScan();
    this.closed.emit('cancelled');
  }

  // ─── Private ─────────────────────────────────────────────────────────────────

  /**
   * Calls `ScanService.scan()` and emits the appropriate output event.
   * A `null` result with `scanState === 'idle'` means the user cancelled via
   * `onCancel()` — no event is emitted (it was already emitted synchronously).
   */
  private async doScan(): Promise<void> {
    const result = await this.scanSvc.scan(this.videoRef?.nativeElement);
    if (result) {
      this.scanned.emit(result);
    } else if (this.scanSvc.scanState() !== 'idle') {
      this.closed.emit('error');
    }
  }
}
